import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext, Profile } from './auth-context-core';
import {
  signInWithPassword as signInWithPasswordService,
  signOutUser,
  signUpWithVerification,
  verifyEmailCode as verifyEmailCodeService,
  resendVerificationCode as resendVerificationCodeService,
} from '@/services/auth/authService';
import { fetchUserProfile, fetchUserRole } from '@/services/auth/profileService';
import { hasActiveSubscription as hasActiveSubscriptionService } from '@/services/auth/subscriptionService';

// ============================================================================
// AUTH PROVIDER
// ============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionLoaded, setSubscriptionLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [roleLoaded, setRoleLoaded] = useState(false); // ✅ Novo estado

  // Ref para evitar fetches duplicados
  const lastLoadedUserIdRef = useRef<string | null>(null);
  const isLoadingDataRef = useRef(false);

  if (import.meta.env.DEV) console.log('[AuthContext] Provider initialized. State:', { user: !!user, loading });

  // ============================================================================
  // CORE FUNCTION: Fetch All User Data (SINGLE QUERY)
  // ============================================================================

  const fetchAllUserData = useCallback(async (userId: string): Promise<boolean> => {
    if (import.meta.env.DEV) console.log('[AuthContext] fetchAllUserData START', { userId });

    // Evitar fetches duplicados simultâneos
    if (isLoadingDataRef.current && lastLoadedUserIdRef.current === userId) {
      if (import.meta.env.DEV) console.log('⏭️ Fetch já em andamento para este usuário - SKIP');
      return false;
    }

    isLoadingDataRef.current = true;

    // ATOMIC RESET: Clean everything before new fetch
    setIsAdmin(false);
    setProfile(null);
    setHasActiveSubscription(false);
    setRoleLoaded(false);
    setSubscriptionLoaded(false);

    const fetchDataPromise = async () => {
      try {
        if (import.meta.env.DEV) console.log('[AuthContext] Parallel fetch START:', userId);

        const [finalRole, mappedProfile, hasActive] = await Promise.all([
          fetchUserRole(userId),
          fetchUserProfile(userId),
          hasActiveSubscriptionService(userId),
        ]);

        const isAd = finalRole?.trim() === 'admin';
        setIsAdmin(isAd);

        setProfile(mappedProfile);

        setHasActiveSubscription(isAd || hasActive);

        lastLoadedUserIdRef.current = userId;
        if (import.meta.env.DEV) console.log('[AuthContext] Parallel fetch DONE');
        return true;
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('❌ Data fetching error:', error);
        }
        return false;
      } finally {
        isLoadingDataRef.current = false;
        setLoading(false);
        setRoleLoaded(true);
        setSubscriptionLoaded(true);
      }
    };

    fetchDataPromise();
    return true;
  }, []);

  // ============================================================================
  // REFRESH PROFILE (Public API)
  // ============================================================================

  // ============================================================================
  // REFRESH PROFILE (Public API)
  // ============================================================================

  const refreshProfile = useCallback(async () => {
    if (!user) {
      console.warn('⚠️ refreshProfile chamado sem usuário logado');
      return;
    }

    if (import.meta.env.DEV) console.log('🔄 Refresh manual de dados do perfil');
    lastLoadedUserIdRef.current = null; // Forçar reload
    await fetchAllUserData(user.id);
  }, [user, fetchAllUserData]); // Dependência: user (fetchAllUserData é estável ou interna)

  // ============================================================================
  // AUTH METHODS
  // ============================================================================

  const isEmailVerified = useMemo(() => {
    if (!user) return false;
    // Check both user confirmation and profile status
    return (
      user.email_confirmed_at !== null ||
      user.app_metadata?.email_confirmed === true ||
      profile?.is_verified === true
    );
  }, [user, profile]);

  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    try {
      if (import.meta.env.DEV) console.log('📝 Iniciando cadastro para:', email);
      return await signUpWithVerification(email, password, fullName);
    } catch (error) {
      const errorMessage = error instanceof Error ? error : new Error('Erro desconhecido');
      console.error('❌ Exceção no signUp:', errorMessage);
      return { error: errorMessage };
    }
  }, []); // Sem dependências externas mutáveis

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      if (import.meta.env.DEV) console.log('🔐 Iniciando login para:', email);
      return await signInWithPasswordService(email, password);
    } catch (error) {
      const errorMessage = error instanceof Error ? error : new Error('Erro desconhecido');
      console.error('❌ Exceção no signIn:', errorMessage);
      return { error: errorMessage };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      if (import.meta.env.DEV) console.log('🚪 Fazendo logout...');

      await signOutUser();

      // Limpar estados
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      setHasActiveSubscription(false);
      setSubscriptionLoaded(false);
      lastLoadedUserIdRef.current = null;

      if (import.meta.env.DEV) console.log('✅ Logout concluído');
    } catch (error) {
      console.error('❌ Erro ao fazer logout:', error);
    }
  }, []);

  const verifyEmailCode = useCallback(async (
    code: string,
    userId?: string,
    email?: string,
    password?: string
  ) => {
    try {
      if (import.meta.env.DEV) console.log('🔍 Verificando código:', code);

      const userIdToVerify = userId || user?.id;

      if (!userIdToVerify) {
        return { error: new Error('ID do usuário não fornecido') };
      }

      const result = await verifyEmailCodeService(userIdToVerify, code);

      if (result.error) {
        console.error('❌ Verificação falhou:', result.error);
        return result;
      }

      if (import.meta.env.DEV) console.log('✅ Email verificado com sucesso');

      // Se temos email e password, fazer login automático
      if (email && password) {
        if (import.meta.env.DEV) console.log('🔐 Fazendo login automático após verificação');
        return await signIn(email, password);
      }

      return { error: null };
    } catch (error) {
      const errorMessage = error instanceof Error ? error : new Error('Erro desconhecido');
      console.error('❌ Exceção ao verificar código:', errorMessage);
      return { error: errorMessage };
    }
  }, [user, signIn]); // Depende de User e SignIn

  const resendVerificationCode = useCallback(async (email: string, userId?: string) => {
    try {
      if (import.meta.env.DEV) console.log('📧 Reenviando código para:', email);
      const result = await resendVerificationCodeService(email, userId || user?.id);
      if (result.error) {
        return result;
      }

      if (import.meta.env.DEV) console.log('✅ Código reenviado com sucesso');
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error : new Error('Erro desconhecido');
      console.error('❌ Exceção ao reenviar código:', errorMessage);
      return { error: errorMessage };
    }
  }, [user]);

  // ============================================================================
  // AUTH STATE LISTENER
  // ============================================================================

  useEffect(() => {
    let mounted = true;
    if (import.meta.env.DEV) console.log('[AuthContext] Initializing Auth State');

    const handleAuthChange = async (event: string, session: Session | null) => {
      if (!mounted) return;
      if (import.meta.env.DEV) console.log('[AuthContext] Auth Event Triggered:', event, session?.user?.email || 'No User');

      if (event === 'SIGNED_OUT' || !session) {
        setUser(null); setSession(null); setProfile(null);
        setIsAdmin(false); setHasActiveSubscription(false);
        setSubscriptionLoaded(false); setRoleLoaded(false);
        setLoading(false);
        lastLoadedUserIdRef.current = null;
        return;
      }

      setSession(session);
      setUser(session.user);

      // Fetch data only if missing or user changed
      if (lastLoadedUserIdRef.current !== session.user.id || !roleLoaded) {
        await fetchAllUserData(session.user.id);
      } else {
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') {
        setSession(session);
        return;
      }
      handleAuthChange(event, session);
    });

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        if (session) {
          handleAuthChange('INITIAL_SESSION', session);
        } else {
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchAllUserData, roleLoaded]);

  // ============================================================================
  // CONTEXT PROVIDER
  // ============================================================================

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isAdmin,
        hasActiveSubscription,
        subscriptionLoaded,
        loading,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        roleLoaded,
        isEmailVerified,
        verifyEmailCode,
        resendVerificationCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Custom Hook moved to hooks/use-auth.ts
